import { useEffect, lazy, Suspense } from "react";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import Jobs from "./routes/home/Jobs/Jobs";
import JobDetails from "./routes/job/JobDetails";
import JobInfo from "./routes/job/JobInfo";
import JobComments from "./routes/job/JobComments";
import JobPhotos from "./routes/job/JobPhotos";
import JobPhotoListing from "./routes/job/JobPhotoListing";
import JobPhotoUpload from "./routes/job/JobPhotoUpload";
import UserProfile from "./routes/userProfile/UserProfile";
import UserSettings from "./routes/userProfile/UserSettings";
import UserPassword from "./routes/userProfile/UserPassword";
import CreateJob from "./routes/job/CreateJob";
import EditJob from "./routes/job/EditJob";
import JobAssignments from "./routes/job/JobAssignments";
import CompleteList from "./routes/job/CompleteList";

import Airports from "./routes/airports/Airports";
import AirportDetails from "./routes/airports/AirportDetails";
import CreateAirport from "./routes/airports/CreateAirport";
import MyAirports from "./routes/airports/MyAirports";

import CustomerServiceReport from "./routes/home/dashboard/CustomerServiceReport";

import InventoryList from "./routes/inventory/InventoryList";
import CreateItem from "./routes/inventory/CreateItem";
import ItemDetails from "./routes/inventory/ItemDetails";
import InventoryDashboard from "./routes/inventory/InventoryDashboard";
import InventoryCurrentStats from "./routes/inventory/InventoryCurrentStats";
import InventoryHistoricalStats from "./routes/inventory/InventoryHistoricalStats";
import InventoryGrowth from "./routes/inventory/InventoryGrowth";
import LocationNotifications from "./routes/inventory/LocationNotifications";
import CreateLocation from "./routes/inventory/CreateLocation";

import Customers from "./routes/customers/Customers";
import CustomerDetails from "./routes/customers/CustomerDetails";
import CustomerServices from "./routes/customers/CustomerServices";
import CustomerDiscounts from "./routes/customers/CustomerDiscounts";
import CustomerDiscountList from "./routes/customers/CustomerDiscountList";
import CustomerAddDiscount from "./routes/customers/CustomerAddDiscount";
import CustomerEditDiscount from "./routes/customers/CustomerEditDiscount";
import CustomerFees from "./routes/customers/CustomerFees";
import CustomerFeeList from "./routes/customers/CustomerFeeList";
import CustomerAddFee from "./routes/customers/CustomerAddFee";
import CustomerEditFee from "./routes/customers/CustomerEditFee";
import CustomerJobs from "./routes/customers/CustomerJobs";
import CustomerProfile from "./routes/customers/CustomerProfile";
import CustomerFollowerEmails from "./routes/customers/CustomerFollowerEmails";
import CustomerCategories from "./routes/customers/CustomerCategories";
import CustomerActivity from "./routes/customers/CustomerActivity";

import CreateCustomer from "./routes/customers/CreateCustomer";
import EditCustomer from "./routes/customers/EditCustomer";
import JobReview from "./routes/job/JobReview";
import JobActivityFeed from "./routes/job/JobActivityFeed";
import JobPriceBreakdown from "./routes/job/JobPricebreakdown";
import ServicePrices from "./routes/pricePlans/ServicePrices";
import PricePlans from "./routes/pricePlans/PricePlans";
import CreatePricePlan from "./routes/pricePlans/CreatePricePlan";
import PricePlanDetails from "./routes/pricePlans/PricePlanDetails";

import Vendors from "./routes/vendors/Vendors";
import VendorProfile from "./routes/vendors/VendorProfile";
import VendorDetails from "./routes/vendors/VendorDetails";
import CreateVendor from "./routes/vendors/CreateVendor";
import EditVendor from "./routes/vendors/EditVendor";
import VendorFiles from "./routes/vendors/VendorFiles";

import JobTailDetails from "./routes/job/JobTailDetails";

import NotFound from "./routes/notfound/NotFound";
import Layout from "./layout/Layout";
import AccessBoundary from "./routes/airportBadges/AccessBoundary";
import Scheduler from "./routes/scheduler/Scheduler";
import AirportBadges from "./routes/airportBadges/AirportBadges";
import Footer from "./components/footer/Footer";
import ChangeLog from "./routes/changeLog/ChangeLog";
import TermsAndConditions from "./routes/termsAndConditions/TermsAndConditions";

import Locations from "./routes/public/Locations";
import PublicSite from "./routes/public/site/PublicSite";

import CustomerHome from "./routes/home/customer/CustomerHome";
import CustomerPremium from "./routes/home/customer/CustomerPremium";
import CustomerEditJob from "./routes/job/CustomerEditJob";

import Users from "./routes/users/Users";
import UserDetails from "./routes/users/UserDetails";
import CreateUser from "./routes/users/CreateUser";

import TeamProductivity from "./routes/home/dashboard/TeamProductivity";
import UserProductivity from "./routes/home/dashboard/UserProductivity";
import TailNumberReport from "./routes/home/dashboard/TailNumberReport";
import ServicesByAirport from "./routes/home/dashboard/ServicesByAirport";
import RetainerCustomers from "./routes/home/dashboard/RetainerCustomers";

import CustomerTails from "./routes/home/dashboard/CustomerTails";

import CreateEstimate from "./routes/estimates/CreateEstimate";
import Estimates from "./routes/estimates/Estimates";
import EstimateDetail from "./routes/estimates/EstimateDetail";

import Fees from "./routes/fees/Fees";

import CreateJobSchedule from "./routes/job/CreateJobSchedule";
import JobSchedules from "./routes/job/JobSchedules";

import SharedLayout from "./routes/shared/SharedLayout";
import SharedJob from "./routes/shared/SharedJob";
import ShareJobEstimate from "./routes/shared/SharedJobEstimate";
import SharedJobConfirm from "./routes/shared/SharedJobConfirm";
import SharedJobAccept from "./routes/shared/SharedJobAccept";

import ApiDocumentation from "./routes/shared/apiDocumentation/ApiDocumentation";
import Introduction from "./routes/shared/apiDocumentation/Introduction";
import ServicesApiDoc from "./routes/shared/apiDocumentation/ServicesApiDoc";
import CreateJobApiDoc from "./routes/shared/apiDocumentation/CreateJobApiDoc";
import JobDetailsApiDoc from "./routes/shared/apiDocumentation/JobDetailsApiDoc";
import JobCommentsApiDoc from "./routes/shared/apiDocumentation/JobCommentsApiDoc";
import JobPriceBreakdownApiDoc from "./routes/shared/apiDocumentation/JobPriceBreakdownApiDoc";
import JobPhotosApiDoc from "./routes/shared/apiDocumentation/JobPhotosApiDoc";
import AirportsApiDoc from "./routes/shared/apiDocumentation/AirportsApiDoc";
import FboSearchApiDoc from "./routes/shared/apiDocumentation/FboSearchApiDoc";
import CustomerServicesByNameApiDoc from "./routes/shared/apiDocumentation/CustomerServicesByNameApiDoc";

import ExportJobs from "./routes/job/ExportJobs";

import TailAlerts from "./routes/alerts/TailAlerts";

import ContactUs from "./routes/shared/ContactUs";

import HelpFileList from "./routes/help/HelpFileList";

import ResetPassword from "./routes/shared/ResetPassword";
import CustomerReactivation, { InactiveCustomer } from "./routes/shared/CustomerReactivation";

import { isUserAuthenticated } from "./localstorage";
import { selectUser } from "./routes/userProfile/userSlice";
import { useAppSelector } from "./app/hooks";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./App.css";

import { pageview } from "./gtag";

const GA_TRACKING_ID = "G-P251G6PCFY";

function useGoogleAnalytics(recoveryPage) {
  useEffect(() => {
    if (recoveryPage || process.env.REACT_APP_DISABLE_ANALYTICS === "true" ||
        ["localhost", "127.0.0.1"].includes(window.location.hostname)) return;
    // Create the script tag
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag; // Attach gtag to window (important)

    gtag("js", new Date());
    gtag("config", GA_TRACKING_ID);
  }, [recoveryPage]);
}

const Redirect = () => {
  useEffect(() => {
    window.location = "/login";
  }, []);

  return <h5>Redirecting...</h5>;
};

const ProtectedRoute = () => {
  if (!isUserAuthenticated()) {
    return <Redirect />;
  }

  return <Outlet />;
};

const Fallback = () => {
  return <div></div>;
};

const App = () => {
  const location = useLocation();
  const isRecoveryPage = location.pathname.startsWith("/shared/reactivate-account/");
  useGoogleAnalytics(isRecoveryPage);
  const currentUser = useAppSelector(selectUser);
  const isPublicCloseout = /^\/shared\/jobs\/[^/]+\/?$/.test(
    location.pathname,
  );
  const isPublicSite = ["/login", "/signup", "/platform", "/services", "/locations", "/integrations", "/insights", "/insights/coordinating-aircraft-cleaning-across-airports", "/vendors/apply", "/shared/contact", "/legal", "/terms", "/vendor-terms", "/privacy-policy", "/sms-terms", "/disinsections"].includes(location.pathname.replace(/\/$/, ""));

  useEffect(() => {
    if (!isRecoveryPage && process.env.REACT_APP_DISABLE_ANALYTICS !== "true" &&
        !["localhost", "127.0.0.1"].includes(window.location.hostname)) {
      pageview(location.pathname + location.search);
    }
  }, [location, isRecoveryPage]);

  return (
    <>
      <Suspense fallback={<Fallback />}>
        <ToastContainer autoClose={2000} />
        {/* You need to have a wrapper here with a content and a footer for the footer to be sticky */}
        <div className="flex flex-col min-h-screen">
          <div className="flex-grow">
            <AnimatePresence mode="wait">
              {/* This key location causes to re-renders the parent component. Making unnecessary api calls */}
              {/* <Routes key={location.pathname} location={location}> */}
              <Routes>
                <Route path="/login" element={<PublicSite page="home" />} />
                <Route path="/signup" element={<PublicSite page="signup" />} />
                <Route path="/privacy-policy" element={<PublicSite page="privacy" />} />
                <Route
                  path="/terms-and-conditions"
                  element={<TermsAndConditions />}
                />

                <Route path="/platform" element={<PublicSite page="platform" />} />
                <Route path="/services" element={<PublicSite page="services" />} />

                <Route path="/locations" element={<Locations />} />
                <Route path="/integrations" element={<PublicSite page="integrations" />} />
                <Route path="/insights" element={<PublicSite page="insights" />} />
                <Route path="/insights/coordinating-aircraft-cleaning-across-airports" element={<PublicSite page="article" />} />
                <Route path="/vendors/apply" element={<PublicSite page="vendor" />} />
                <Route path="/shared/contact" element={<PublicSite page="contact" />} />
                <Route path="/legal" element={<PublicSite page="legal" />} />
                <Route path="/terms" element={<PublicSite page="terms" />} />
                <Route path="/vendor-terms" element={<PublicSite page="vendorTerms" />} />
                <Route path="/sms-terms" element={<PublicSite page="smsTerms" />} />
                <Route path="/disinsections" element={<PublicSite page="disinsections" />} />

                <Route path="/shared" element={<SharedLayout />}>
                  <Route path="account-inactive" element={<InactiveCustomer />} />
                  <Route path="reactivate-account/:uid/:token" element={<CustomerReactivation />} />
                  <Route
                    path="api-documentation"
                    element={<ApiDocumentation />}
                  >
                    <Route index element={<Introduction />} />
                    <Route path="introduction" element={<Introduction />} />
                    <Route path="services" element={<ServicesApiDoc />} />
                    <Route
                      path="customer-services-by-name"
                      element={<CustomerServicesByNameApiDoc />}
                    />
                    <Route path="airports" element={<AirportsApiDoc />} />
                    <Route path="fbos" element={<FboSearchApiDoc />} />
                    <Route path="create-job" element={<CreateJobApiDoc />} />
                    <Route path="job-details" element={<JobDetailsApiDoc />} />
                    <Route
                      path="job-comments"
                      element={<JobCommentsApiDoc />}
                    />
                    <Route
                      path="job-price-breakdown"
                      element={<JobPriceBreakdownApiDoc />}
                    />
                    <Route path="job-photos" element={<JobPhotosApiDoc />} />
                  </Route>
                  <Route path="jobs/:encoded_id" element={<SharedJob />} />
                  <Route
                    path="jobs/:encoded_id/confirm"
                    element={<SharedJobConfirm />}
                  />
                  <Route
                    path="jobs/:encoded_id/accept"
                    element={<SharedJobAccept />}
                  />
                  <Route
                    path="estimates/:encoded_id"
                    element={<ShareJobEstimate />}
                  />
                  <Route
                    path="reset-password/:uid/:token"
                    element={<ResetPassword />}
                  />
                </Route>

                <Route element={<AccessBoundary />}>
                  <Route path="/" element={<Layout />}>
                    <Route path="scheduler" element={<Scheduler />} />
                    <Route path="airport-badges" element={<AirportBadges />} />
                    {currentUser.isCustomer && (
                      <>
                        <Route index element={<CustomerHome />} />
                      </>
                    )}

                    <Route path="home" element={<CustomerHome />} />

                    {!currentUser.isCustomer && (
                      <Route index element={<Jobs />} />
                    )}

                    <Route path="premium" element={<CustomerPremium />} />
                    <Route
                      path="create-estimate"
                      element={<CreateEstimate />}
                    />
                    <Route path="estimates" element={<Estimates />} />
                    <Route path="estimates/:id" element={<EstimateDetail />} />

                    <Route path="fees" element={<Fees />} />

                    <Route path="jobs" element={<Jobs />} />

                    <Route path="export-jobs" element={<ExportJobs />} />

                    <Route
                      path="service-report"
                      element={<CustomerServiceReport />}
                    />

                    <Route path="vendor-files" element={<VendorFiles />} />

                    <Route
                      path="team-productivity"
                      element={<TeamProductivity />}
                    />

                    <Route path="customer-tails" element={<CustomerTails />} />

                    <Route
                      path="user-productivity/:id"
                      element={<UserProductivity />}
                    />
                    <Route path="tail-report" element={<TailNumberReport />} />
                    <Route
                      path="services-by-airport"
                      element={<ServicesByAirport />}
                    />
                    <Route path="retainers" element={<RetainerCustomers />} />

                    <Route path="create-job" element={<CreateJob />} />

                    <Route
                      path="create-schedule"
                      element={<CreateJobSchedule />}
                    />

                    <Route path="jobs/schedules" element={<JobSchedules />} />

                    <Route
                      path="create-job/:estimateId"
                      element={<CreateJob />}
                    />

                    <Route path="completed" element={<CompleteList />} />
                    <Route
                      path="completed/review/:jobId"
                      element={<JobReview />}
                    >
                      <Route path="photos" element={<JobPhotos />}>
                        <Route index element={<JobPhotoListing />} />
                        <Route path="listing" element={<JobPhotoListing />} />
                        <Route path="upload" element={<JobPhotoUpload />} />
                      </Route>
                      <Route path="edit" element={<EditJob />} />
                      <Route
                        path="customer-edit"
                        element={<CustomerEditJob />}
                      />
                      <Route path="activity" element={<JobActivityFeed />} />
                      <Route path="comments" element={<JobComments />} />
                      <Route path="services" element={<JobAssignments />} />
                    </Route>

                    <Route path="report/review/:jobId" element={<JobReview />}>
                      <Route path="photos" element={<JobPhotos />}>
                        <Route index element={<JobPhotoListing />} />
                        <Route path="listing" element={<JobPhotoListing />} />
                        <Route path="upload" element={<JobPhotoUpload />} />
                      </Route>
                      <Route path="edit" element={<EditJob />} />
                      <Route path="activity" element={<JobActivityFeed />} />
                      <Route path="comments" element={<JobComments />} />
                      <Route path="services" element={<JobAssignments />} />
                    </Route>

                    <Route
                      path="create-job/review/:jobId"
                      element={<JobReview />}
                    >
                      <Route path="photos" element={<JobPhotos />}>
                        <Route index element={<JobPhotoListing />} />
                        <Route path="listing" element={<JobPhotoListing />} />
                        <Route path="upload" element={<JobPhotoUpload />} />
                      </Route>
                      <Route path="edit" element={<EditJob />} />
                      <Route path="activity" element={<JobActivityFeed />} />
                      <Route path="comments" element={<JobComments />} />
                      <Route path="services" element={<JobAssignments />} />
                    </Route>

                    <Route path="users" element={<Users />}>
                      {/* <Route path=":userId" element={<UserDetails />} /> */}
                      <Route
                        path=":id/productivity"
                        element={<UserProductivity />}
                      />
                    </Route>
                    <Route path="users/:userId" element={<UserDetails />} />
                    <Route path="create-user" element={<CreateUser />} />

                    <Route path="airports" element={<Airports />}>
                      <Route path=":airportId" element={<AirportDetails />} />
                    </Route>

                    <Route path="my-airports" element={<MyAirports />} />

                    <Route path="create-airport" element={<CreateAirport />} />

                    <Route
                      path="create-customer"
                      element={<CreateCustomer />}
                    />
                    <Route
                      path="edit-customer/:customerId"
                      element={<EditCustomer />}
                    />

                    <Route path="create-vendor" element={<CreateVendor />} />
                    <Route
                      path="edit-vendor/:vendorId"
                      element={<EditVendor />}
                    />

                    <Route path="customers" element={<Customers />}>
                      <Route
                        path=":customerId/profile"
                        element={<CustomerProfile />}
                      >
                        <Route index element={<CustomerDetails />} />
                        <Route path="details" element={<CustomerDetails />} />

                        <Route path="services" element={<CustomerServices />} />

                        <Route
                          path="emails"
                          element={<CustomerFollowerEmails />}
                        />

                        <Route
                          path="categories"
                          element={<CustomerCategories />}
                        />

                        <Route path="activity" element={<CustomerActivity />} />

                        <Route path="discounts" element={<CustomerDiscounts />}>
                          <Route index element={<CustomerDiscountList />} />
                          <Route path="add" element={<CustomerAddDiscount />} />
                          <Route
                            path="edit/:discountId"
                            element={<CustomerEditDiscount />}
                          />
                        </Route>

                        <Route path="fees" element={<CustomerFees />}>
                          <Route index element={<CustomerFeeList />} />
                          <Route path="add" element={<CustomerAddFee />} />
                          <Route
                            path="edit/:feeId"
                            element={<CustomerEditFee />}
                          />
                        </Route>

                        <Route path="jobs" element={<CustomerJobs />} />
                      </Route>
                    </Route>

                    <Route path="vendors" element={<Vendors />}>
                      <Route
                        path=":vendorId/profile"
                        element={<VendorProfile />}
                      >
                        <Route index element={<VendorDetails />} />
                        <Route path="details" element={<VendorDetails />} />
                        <Route path="files" element={<VendorFiles />} />
                      </Route>
                    </Route>

                    <Route path="user-settings" element={<UserSettings />}>
                      <Route index element={<UserProfile />} />
                      <Route path="profile" element={<UserProfile />} />
                      <Route path="password" element={<UserPassword />} />
                    </Route>

                    <Route path="jobs/:jobId" element={<JobDetails />}>
                      <Route index element={<JobInfo />} />
                      <Route index path="details" element={<JobInfo />} />
                      <Route path="comments" element={<JobComments />} />
                      <Route path="assignments" element={<JobAssignments />} />
                      <Route path="edit" element={<EditJob />} />
                      <Route
                        path="customer-edit"
                        element={<CustomerEditJob />}
                      />
                      <Route path="activity" element={<JobActivityFeed />} />
                      <Route
                        path="price-breakdown"
                        element={<JobPriceBreakdown />}
                      />
                      <Route path="tail-details" element={<JobTailDetails />} />
                      <Route path="photos" element={<JobPhotos />}>
                        <Route
                          index
                          path="listing"
                          element={<JobPhotoListing />}
                        />
                        <Route path="upload" element={<JobPhotoUpload />} />
                      </Route>
                    </Route>

                    <Route path="changelog" element={<ChangeLog />} />
                    <Route path="contact" element={<ContactUs />} />
                    <Route path="help" element={<HelpFileList />} />
                    <Route path="price-plans" element={<PricePlans />} />
                    <Route
                      path="price-plans/:pricePlanId/details"
                      element={<PricePlanDetails />}
                    />
                    <Route
                      path="price-plans/add"
                      element={<CreatePricePlan />}
                    />
                    <Route
                      path="price-plans/:pricePlanId"
                      element={<ServicePrices />}
                    />

                    <Route path="tail-alerts" element={<TailAlerts />} />

                    <Route path="inventory" element={<InventoryList />} />
                    <Route
                      path="create-inventory-item"
                      element={<CreateItem />}
                    />

                    <Route
                      path="inventory/:itemId/details"
                      element={<ItemDetails />}
                    />

                    <Route
                      path="location-notifications"
                      element={<LocationNotifications />}
                    />

                    <Route
                      path="create-location"
                      element={<CreateLocation />}
                    />

                    <Route
                      path="inventory/stats"
                      element={<InventoryDashboard />}
                    >
                      <Route index element={<InventoryCurrentStats />} />
                      <Route
                        path="current"
                        element={<InventoryCurrentStats />}
                      />
                      <Route
                        path="historical"
                        element={<InventoryHistoricalStats />}
                      />
                      <Route path="growth" element={<InventoryGrowth />} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>
              </Routes>
            </AnimatePresence>
          </div>
          {!isPublicSite && !isPublicCloseout && <Footer />}
        </div>
      </Suspense>
    </>
  );
};

export default App;
